import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface UseTabFromUrlOptions {
  paramName?: string;
  defaultTab?: number;
  tabMapping?: Record<string, number>;
}

export interface UseTabFromUrlReturn {
  tabValue: number;
  setTabValue: (tab: number) => void;
  updateUrlTab: (tab: number) => void;
}

/**
 * Hook for managing tab state synchronized with URL parameters
 * Reads initial tab from URL and updates URL when tab changes
 * 
 * @param options - Configuration for tab management
 * @returns Tab state and update functions
 * 
 * @example
 * // Simple usage with default param name 'tab'
 * const { tabValue, setTabValue } = useTabFromUrl({
 *   defaultTab: 0
 * });
 * 
 * @example
 * // With custom tab mapping
 * const { tabValue, setTabValue } = useTabFromUrl({
 *   paramName: 'view',
 *   tabMapping: {
 *     'editor': 0,
 *     'all': 1,
 *     'places': 2,
 *     'neighborhood-editor': 3
 *   },
 *   defaultTab: 0
 * });
 */
export const useTabFromUrl = ({
  paramName = 'tab',
  defaultTab = 0,
  tabMapping,
}: UseTabFromUrlOptions = {}): UseTabFromUrlReturn => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get initial tab from URL
  const getInitialTab = useCallback(() => {
    const tabParam = searchParams.get(paramName);
    if (!tabParam) return defaultTab;
    
    if (tabMapping) {
      return tabMapping[tabParam] ?? defaultTab;
    }
    
    // Try to parse as number
    const tabNum = parseInt(tabParam, 10);
    return isNaN(tabNum) ? defaultTab : tabNum;
  }, [searchParams, paramName, defaultTab, tabMapping]);

  const [tabValue, setTabValueState] = useState<number>(getInitialTab);

  // Update tab when URL params change
  useEffect(() => {
    const newTab = getInitialTab();
    setTabValueState(newTab);
  }, [getInitialTab]);

  // Update URL when tab changes
  const updateUrlTab = useCallback((tab: number) => {
    const newSearchParams = new URLSearchParams(searchParams);
    
    if (tabMapping) {
      // Find the key for this tab value
      const tabKey = Object.entries(tabMapping).find(([_, value]) => value === tab)?.[0];
      if (tabKey) {
        newSearchParams.set(paramName, tabKey);
      } else {
        newSearchParams.delete(paramName);
      }
    } else {
      newSearchParams.set(paramName, String(tab));
    }
    
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams, paramName, tabMapping]);

  const setTabValue = useCallback((tab: number) => {
    setTabValueState(tab);
    updateUrlTab(tab);
  }, [updateUrlTab]);

  return {
    tabValue,
    setTabValue,
    updateUrlTab,
  };
};
